import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import BuySellAccountLookup from './BuySellAccountLookup.jsx';
import BuySellDafUpload from './BuySellDafUpload.jsx';
import { getTodayIsoDate, isDafFileTooLarge, MAX_DAF_MB } from './buySellUtils.js';
import useBuySellAccountSearch from './useBuySellAccountSearch.js';

const BuySellFormDialog = ({ open, onClose, onSubmit, stageOptions, onSearch, isSubmitting = false }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [stage, setStage] = useState('DAF');
  const [stageDate, setStageDate] = useState(() => getTodayIsoDate());
  const [buySellAccountId, setBuySellAccountId] = useState('');
  const [purchasingBAC, setPurchasingBAC] = useState('');
  const [notes, setNotes] = useState('');
  const [dafFile, setDafFile] = useState(null);
  const [dafError, setDafError] = useState('');
  const sellingSearch = useBuySellAccountSearch({ open, onSearch });
  const purchasingSearch = useBuySellAccountSearch({ open, onSearch });
  const { resetSearch: resetSellingSearch } = sellingSearch;
  const { resetSearch: resetPurchasingSearch, selectedAccount: selectedPurchasingAccount } = purchasingSearch;

  const isDafStage = stage === 'DAF';

  useEffect(() => {
    if (!open) return;
    resetSellingSearch();
    resetPurchasingSearch();
    setStage('DAF');
    setStageDate(getTodayIsoDate());
    setBuySellAccountId('');
    setPurchasingBAC('');
    setNotes('');
    setDafFile(null);
    setDafError('');
  }, [open, resetPurchasingSearch, resetSellingSearch]);

  useEffect(() => {
    if (!selectedPurchasingAccount) return;
    setBuySellAccountId(selectedPurchasingAccount.accountId || '');
    if (String(selectedPurchasingAccount.bac || '').trim()) {
      setPurchasingBAC(selectedPurchasingAccount.bac);
    }
  }, [selectedPurchasingAccount]);

  useEffect(() => {
    if (!isDafStage) {
      setDafFile(null);
      setDafError('');
    }
  }, [isDafStage]);

  const canSubmit = useMemo(() => {
    if (!sellingSearch.selectedAccount?.accountName || !sellingSearch.selectedAccount?.accountId) return false;
    if (!String(sellingSearch.selectedAccount?.bac || '').trim() && !purchasingBAC.trim()) return false;
    if (!stage) return false;
    if (!stageDate) return false;
    return true;
  }, [purchasingBAC, sellingSearch.selectedAccount, stage, stageDate]);

  const handleSubmit = () => {
    if (!canSubmit || isSubmitting) return;
    onSubmit?.({
      accountName: sellingSearch.selectedAccount.accountName,
      accountId: sellingSearch.selectedAccount.accountId,
      bac: sellingSearch.selectedAccount.bac,
      purchasingBAC: purchasingBAC.trim(),
      buySellAccountId: buySellAccountId.trim(),
      buySellStage: stage,
      stageDate,
      notes: notes.trim(),
      dafFile,
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      setDafFile(null);
      setDafError('');
      return;
    }
    if (file.type && file.type !== 'application/pdf') {
      setDafFile(null);
      setDafError('Only PDF uploads are supported.');
      return;
    }
    if (isDafFileTooLarge(file)) {
      setDafFile(null);
      setDafError(`File exceeds ${MAX_DAF_MB} MB.`);
      return;
    }
    setDafFile(file);
    setDafError('');
  };

  const handleSellingAccountChange = (next) => {
    sellingSearch.handleAccountChange(next);
  };

  const handlePurchasingAccountChange = (next) => {
    purchasingSearch.handleAccountChange(next);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isSmallScreen}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          height: { xs: '100dvh', sm: 'auto' },
          maxHeight: { xs: '100dvh', sm: 'calc(100% - 64px)' },
          display: 'grid',
          gridTemplateRows: 'auto minmax(0, 1fr) auto',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle>Add Buy/Sell</DialogTitle>
      <DialogContent
        dividers
        data-testid="buy-sell-form-content"
        sx={{
          gridRow: 2,
          minHeight: 0,
          overflow: 'auto',
          position: 'relative',
          zIndex: 0,
          contain: 'paint',
          pb: { xs: 10, sm: 2 },
        }}
      >
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <BuySellAccountLookup
            searchOptions={sellingSearch.searchOptions}
            searchLoading={sellingSearch.searchLoading}
            searchInputValue={sellingSearch.inputValue}
            onSearchInputValueChange={sellingSearch.setInputValue}
            onSearchQueryChange={sellingSearch.setSearchQuery}
            selectedAccount={sellingSearch.selectedAccount}
            onAccountChange={handleSellingAccountChange}
            purchasingSearchOptions={purchasingSearch.searchOptions}
            purchasingSearchLoading={purchasingSearch.searchLoading}
            purchasingSearchInputValue={purchasingSearch.inputValue}
            onPurchasingSearchInputValueChange={purchasingSearch.setInputValue}
            onPurchasingSearchQueryChange={purchasingSearch.setSearchQuery}
            purchasingAccount={purchasingSearch.selectedAccount}
            onPurchasingAccountChange={handlePurchasingAccountChange}
            buySellAccountId={buySellAccountId}
            onBuySellAccountIdChange={setBuySellAccountId}
            purchasingBAC={purchasingBAC}
            onPurchasingBACChange={setPurchasingBAC}
          />

          <FormControl fullWidth>
            <InputLabel id="buy-sell-stage-label">Stage</InputLabel>
            <Select
              labelId="buy-sell-stage-label"
              label="Stage"
              value={stage}
              onChange={(event) => setStage(event.target.value)}
            >
              {stageOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>DAF = initial notification, D2C2 = inventory feed ready.</FormHelperText>
          </FormControl>

          <TextField
            fullWidth
            label="Stage date"
            type="date"
            value={stageDate}
            onChange={(event) => setStageDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            label="Notes"
            multiline
            minRows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            helperText="Optional. Shown as SPECIAL NOTES in the email when provided."
          />

          <BuySellDafUpload
            show={isDafStage}
            description="Upload the DAF PDF (optional but recommended)."
            dafFile={dafFile}
            error={dafError}
            onFileChange={handleFileChange}
            onClearFile={() => setDafFile(null)}
            missingWarning={!dafFile && !dafError}
            missingWarningText="DAF is missing; email will send with a warning."
          />
        </Stack>
      </DialogContent>
      <DialogActions
        data-testid="buy-sell-form-actions"
        sx={{
          gridRow: 3,
          bgcolor: 'background.paper',
        }}
      >
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        >
          Add buy/sell
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BuySellFormDialog;
